import { Asset, Button, Checkbox, InfoIcon, Modal } from '@clearcover/atlas';
import { CreatePolicyReinstatementResponseType } from '@clearcover/core-app-types';
import { Formik } from 'formik';
import { PolicyPaymentMethod } from 'Policies/components/PolicyPaymentMethod';
import React, { FC, useCallback, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ConfirmationModal, FormInput } from 'Shared/components';
import { useLazyApi } from 'Shared/services/Api';
import { formatMoney, isPresent } from 'Shared/utils';
import * as Yup from 'yup';

import { DateTime } from 'luxon';
import { PolicyContext } from 'Policies/utils';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import styles from '../PolicyCancellationForm/styles.module.css';
import doorStyles from '../styles.module.css';

const ReinstatementFormSchema = Yup.object().shape({
  paymentMethodId: Yup.string().required('Required'),
  reinstateWithoutLapse: Yup.boolean(),
  waiveFee: Yup.boolean(),
  statementOfLossRecieved: Yup.boolean(),
});

const PolicyReinstatementSummary: FC<{
  data: CreatePolicyReinstatementResponseType;
  paymentMethodId: string;
}> = ({ data, paymentMethodId }) => {
  const paymentMethodType =
    data.paymentMethods.find((pm) => pm.id === paymentMethodId)?.type ||
    'creditCard';

  const paymentSummaryFields = [
    {
      label: 'Premium Amount',
      amount: data.premium,
      dataTestId: 'premium',
    },
  ].concat(
    [
      { label: 'Fee Amount', amount: data.fees, dataTestId: 'fees' },
      {
        label: 'Reinstatement Fee',
        amount: data.reinstatementFee,
        dataTestId: 'reinstatement-fee',
      },
      {
        label:
          paymentMethodType === 'creditCard' ? 'Credit Card Fee' : 'ACH Fee',
        amount: data.paymentFeeInfo.fees[paymentMethodType],
        dataTestId: 'payment-fee',
      },
      { label: 'Taxes', amount: data.taxes, dataTestId: 'taxes' },
      {
        label: 'Surplus Contribution',
        amount: data.memberSurplusContribution,
        dataTestId: 'member-surplus-contribution',
      },
    ].filter(({ amount }) => isPresent(amount) && amount !== 0)
  );

  return (
    <section className={styles.paymentSummary}>
      <div className={styles.summary}>
        <h2>Payment Summary</h2>
        {paymentSummaryFields.map(({ label, amount, dataTestId }) => (
          <div
            key={label}
            data-testid={`summary-pair-${dataTestId}`}
            className={styles.summaryPair}
          >
            <div
              className={styles.summaryLabel}
              data-testid={`summary-pair-${dataTestId}-label`}
            >
              {label}
            </div>
            <div
              data-testid={`summary-pair-${dataTestId}-amount`}
              className={styles.summaryAmount}
            >
              {formatMoney(amount)}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.reinstatementTotalAmount}>
        <div className={styles.refundLabel}>Total:</div>
        <div data-testid="total-amount" className={styles.refundAmount}>
          {formatMoney(
            data.paymentFeeInfo.paymentFeeEnabledState
              ? data.totalAmountDueBy[paymentMethodType]
              : data.totalAmountDue
          )}
        </div>
      </div>
    </section>
  );
};

export const PolicyReinstatement: FC<{
  closeDrawer: () => void;
  setCancelPolicyDraftId: (draftId?: string) => void;
  isNSA: boolean;
}> = ({ closeDrawer, setCancelPolicyDraftId, isNSA }) => {
  const { id: policyId } = useParams<{
    id: string;
  }>();
  const {
    details: { timeZone, region },
    refetchHandler,
  } = useContext(PolicyContext);
  const [
    { error, data, loading: loadingReinstatement },
    createReinstatementDraft,
  ] = useLazyApi<CreatePolicyReinstatementResponseType>({
    url: `/policies/${policyId}/createReinstatement`,
  });
  const { underwritingErrors, setUnderwritingErrors } = useUnderwritingErrors();

  const updateReinstatementDraft = useCallback(
    (waiveFee: boolean, reinstateWithoutLapse: boolean) => {
      createReinstatementDraft({
        params: {
          waiveFee,
          reinstateWithoutLapse,
          region,
        },
      })
        .then(({ data }) => {
          setCancelPolicyDraftId(data.draftId);
        })
        .catch((err) => {
          setUnderwritingErrors(err);
        });
    },
    [
      createReinstatementDraft,
      setUnderwritingErrors,
      region,
      setCancelPolicyDraftId,
    ]
  );

  useEffect(() => {
    updateReinstatementDraft(false, isNSA);
  }, [updateReinstatementDraft, isNSA]);

  const [{ loading: applyingReinstatement }, applyReinstatement] = useLazyApi({
    url: `policies/${policyId}/applyReinstatement`,
    method: 'POST',
  });

  const initalizedDate = data
    ? DateTime.fromISO(data.effectiveDate, {
        zone: timeZone,
      })
    : null;
  return (
    <Formik
      initialValues={{
        paymentMethodId:
          data?.paymentMethods.find(
            (method) => method.id === data?.defaultPaymentMethodId
          )?.id || data?.paymentMethods[0]?.id,
        reinstateWithoutLapse: isNSA,
        waiveFee: false,
        statementOfLossRecieved: false,
      }}
      enableReinitialize
      validationSchema={ReinstatementFormSchema}
      onSubmit={(values, { setStatus, setSubmitting }) => {
        const paymentMethodType =
          data?.paymentMethods.find((pm) => pm.id === values.paymentMethodId)
            ?.type || 'creditCard';
        setStatus(null);
        applyReinstatement({
          data: {
            paymentMethodId: values.paymentMethodId,
            reinstateWithoutLapse: values.reinstateWithoutLapse,
            waiveFee: values.waiveFee,
            paymentAmount: data.paymentFeeInfo.paymentFeeEnabledState
              ? data.totalAmountDueBy[paymentMethodType]
              : data.totalAmountDue,
          },
        })
          .then(() => {
            closeDrawer();
            refetchHandler();
          })
          .catch((err) => {
            setSubmitting(false);
            setStatus(
              err.response.data.metadata?.errorMessage ||
                err.response.data.message
            );
          });
      }}
      validateOnMount={true}
      validateOnChange={true}
    >
      {({
        isSubmitting,
        isValid,
        submitForm,
        status,
        setFieldValue,
        values,
      }) => {
        return (
          <div
            data-testid="policy-reinstatement-form"
            className={doorStyles.door}
          >
            <div className={doorStyles.header}>
              <h2>Reinstate Policy</h2>
            </div>
            <div className={doorStyles.body}>
              <div className={styles.radioButtonContainer}>
                <FormInput
                  variant="radio"
                  name="reinstatement-type"
                  className={styles.paymentRadioGroup}
                  value={'with_lapse'}
                  label="Reinstate policy with lapse"
                  disabled={loadingReinstatement || isNSA}
                  checked={values.reinstateWithoutLapse === false}
                  onChecked={() => {
                    updateReinstatementDraft(values.waiveFee, false);
                    setFieldValue('reinstateWithoutLapse', false);
                  }}
                />
              </div>
              <div className={styles.radioButtonContainer}>
                <FormInput
                  variant="radio"
                  name="reinstatement-type"
                  className={styles.paymentRadioGroup}
                  value={'without_lapse'}
                  label="Reinstate policy without lapse"
                  disabled={loadingReinstatement}
                  checked={values.reinstateWithoutLapse === true}
                  onChecked={() => {
                    updateReinstatementDraft(values.waiveFee, true);
                    setFieldValue('reinstateWithoutLapse', true);
                  }}
                />
              </div>
              <Checkbox
                data-testid="waive-fee"
                name="waive-reinstatement-fee"
                value={values.waiveFee}
                label={'Waive reinstatement fee'}
                disabled={loadingReinstatement}
                onChange={() => {
                  updateReinstatementDraft(
                    !values.waiveFee,
                    values.reinstateWithoutLapse
                  );
                  setFieldValue('waiveFee', !values.waiveFee);
                }}
              />
              {initalizedDate && (
                <div className={styles.effectiveDate}>
                  <Asset
                    icon={InfoIcon}
                    description={'Effective Date'}
                    height={16}
                    width={16}
                    className={styles.infoIcon}
                  />
                  <p>
                    This policy will be reinstated on{' '}
                    {initalizedDate.toLocaleString()} at{' '}
                    {initalizedDate.toFormat('ttt')}
                  </p>
                </div>
              )}
              {data && data.paymentMethods.length > 0 && (
                <PolicyPaymentMethod
                  policyPaymentMethods={data.paymentMethods}
                  setValue={(value) => setFieldValue('paymentMethodId', value)}
                  value={values.paymentMethodId || ''}
                />
              )}
              {data && (
                <>
                  <PolicyReinstatementSummary
                    data={data}
                    paymentMethodId={values.paymentMethodId}
                  />

                  {data.paymentMethods.length === 0 && (
                    <p>
                      You must first add a Credit Card as a payment method in
                      order to reinstate a policy.
                    </p>
                  )}
                </>
              )}

              <p className={styles.error}>
                {underwritingErrors.length
                  ? underwritingErrors[0]
                  : error?.message}
              </p>
              <Modal
                toggle={(openModal) => (
                  <Button
                    block
                    data-testid="reinstate-and-pay-policy-button"
                    variant="primary"
                    isSubmitting={isSubmitting}
                    disabled={!isValid || loadingReinstatement || !!error}
                    onClick={() => {
                      openModal();
                      setCancelPolicyDraftId();
                    }}
                  >
                    Make Payment & Reinstate Policy
                  </Button>
                )}
              >
                {(close) => (
                  <ConfirmationModal
                    onSubmit={submitForm}
                    error={status}
                    close={() => {
                      close();
                      setCancelPolicyDraftId(data.draftId);
                    }}
                    isSubmitting={applyingReinstatement}
                    variant="primary"
                    headerText="Please confirm reinstatement"
                    bodyText="This policy will be reinstated. Please confirm that you would like to reinstate this policy."
                  />
                )}
              </Modal>
            </div>
            <Button
              block
              variant="secondary"
              onClick={closeDrawer}
              disabled={loadingReinstatement}
              data-testid="do-not-reinstate-policy-button"
              className={styles.doNotReinstateButton}
            >
              Close Without Reinstatement
            </Button>
          </div>
        );
      }}
    </Formik>
  );
};
